import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useAuth } from "../auth/AuthContext";
import logger from "../../logger";
import {
  fetchAndStoreOrganizations,
  fetchAndStoreOrganizationUnitId,
  getStoredOrganizations,
  getSelectedOrganization,
  setSelectedOrganization as setSelectedOrgStorage,
  getSelectedOrganizationPermissions,
  getOrganizationPermissions,
} from "../services/organizationService";

const OrganizationContext = createContext(null);

export const OrganizationProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [organizationUnitSyncing, setOrganizationUnitSyncing] = useState(false);
  const [organizationUnitSyncingDisplayName, setOrganizationUnitSyncingDisplayName] =
    useState(null);
  const [error, setError] = useState(null);

  const loadOrganizationsFromStorage = useCallback(async () => {
    try {
      const storedOrgs = await getStoredOrganizations();
      const selectedOrg = await getSelectedOrganization();

      setOrganizations(storedOrgs);
      setSelectedOrganization(selectedOrg);

      if (selectedOrg) {
        const orgPermissions = await getSelectedOrganizationPermissions();
        setPermissions(orgPermissions);
      }
    } catch (err) {
      logger.error("Error loading organizations from storage:", err);
    }
  }, []);

  const fetchOrganizations = useCallback(
    async (forceRefresh = false) => {
      if (!isAuthenticated) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        if (!forceRefresh) {
          const storedOrgs = await getStoredOrganizations();
          if (storedOrgs && storedOrgs.length > 0) {
            setOrganizations(storedOrgs);
            const selectedOrg = await getSelectedOrganization();
            setSelectedOrganization(selectedOrg);

            if (selectedOrg) {
              const orgPermissions = await getSelectedOrganizationPermissions();
              setPermissions(orgPermissions);
            }
          }
        }

        const result = await fetchAndStoreOrganizations();
        setOrganizations(result.organizations);

        const selectedOrg = await getSelectedOrganization();
        if (selectedOrg) {
          setSelectedOrganization(selectedOrg);
          const orgPermissions = await getOrganizationPermissions(selectedOrg.id);
          setPermissions(orgPermissions);
          await fetchAndStoreOrganizationUnitId(selectedOrg.id);
        }

        setError(null);
      } catch (err) {
        logger.error("Error fetching organizations:", err);
        setError(err.message || "Failed to fetch organizations");

        await loadOrganizationsFromStorage();
      } finally {
        setLoading(false);
      }
    },
    [isAuthenticated, loadOrganizationsFromStorage]
  );

  const getOrgPermissions = useCallback(async (orgId) => {
    try {
      return await getOrganizationPermissions(orgId);
    } catch (err) {
      logger.error("Error getting organization permissions:", err);
      return [];
    }
  }, []);

  const selectOrganization = useCallback(
    async (orgId, displayName = null) => {
      try {
        setOrganizationUnitSyncing(true);
        const selectedOrg = organizations.find((org) => org.id === orgId) || null;
        const label =
          (typeof displayName === "string" && displayName.trim()) ||
          (selectedOrg ? (selectedOrg.isDR ? `${selectedOrg.name} (DR)` : selectedOrg.name) : null);
        setOrganizationUnitSyncingDisplayName(label);

        await setSelectedOrgStorage(orgId);
        setSelectedOrganization(selectedOrg);

        if (selectedOrg) {
          const orgPermissions = await getOrgPermissions(selectedOrg.id);
          setPermissions(orgPermissions);
          await fetchAndStoreOrganizationUnitId(selectedOrg.id);
        } else {
          setPermissions([]);
        }
      } catch (err) {
        logger.error("Error selecting organization:", err);
      } finally {
        setOrganizationUnitSyncing(false);
        setOrganizationUnitSyncingDisplayName(null);
      }
    },
    [organizations, getOrgPermissions]
  );

  const refreshOrganizations = useCallback(async () => {
    await fetchOrganizations(true);
  }, [fetchOrganizations]);

  useEffect(() => {
    if (isAuthenticated) {
      loadOrganizationsFromStorage();
      fetchOrganizations();
    } else {
      setOrganizations([]);
      setSelectedOrganization(null);
      setPermissions([]);
      setLoading(false);
      setOrganizationUnitSyncing(false);
      setOrganizationUnitSyncingDisplayName(null);
    }
  }, [isAuthenticated, loadOrganizationsFromStorage, fetchOrganizations]);

  const isOrganizationContextPending = loading || organizationUnitSyncing;

  const value = {
    organizations,
    selectedOrganization,
    permissions,
    loading,
    organizationUnitSyncing,
    organizationUnitSyncingDisplayName,
    isOrganizationContextPending,
    error,
    fetchOrganizations,
    selectOrganization,
    refreshOrganizations,
  };

  return <OrganizationContext.Provider value={value}>{children}</OrganizationContext.Provider>;
};

export const useOrganization = () => {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
};

export default OrganizationContext;
